import type { KeyboardEvent } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material';
import {
  ApprovalFormTable,
  ApprovalTableAddRowButton,
  ApprovalTableCellInput,
  ApprovalTableCurrencySelect,
  ApprovalTableRow,
  ApprovalTableRowDeleteButton,
} from '@/components/Approval/@components/ApprovalFormTable';
import { CurrencyLabel } from '@/components/Common/CurrencyFlag';
import { useDialog } from '@/hooks/global-tools';
import PlusIcon from '@/public/icons/approval/plus.svg';
import {
  CURRENCY_OPTIONS,
  formatAmountInput,
} from '@/utils/approval/expensePreApproval';
import {
  computeCurrencyTotals,
  createExpenseVoucherRow,
  type ExpenseVoucherRow,
} from '@/utils/approval/expenseVoucher';

type PayItemTableProps = {
  rows: ExpenseVoucherRow[];
  onChange: (rows: ExpenseVoucherRow[]) => void;
};

const COLUMN_COUNT = 6;

// 작성 표는 셀 여백이 없어(입력칸이 셀을 채움) 합계 줄만 여백을 줌
const TableWrap = styled('div')({
  '& .expense-voucher-item-table tfoot td': { padding: '0 12px' },
});

const focusCell = (table: HTMLElement | null, row: number, col: number) => {
  const cell = table?.querySelector(
    `[data-row-index="${row}"] [data-col-index="${col}"]`,
  );
  const target = cell?.querySelector<HTMLElement>('input, [role="combobox"]');
  if (!target) return false;
  target.focus();
  if (target instanceof HTMLInputElement) target.select();
  return true;
};

/** 지급 내역 표 — 기본 3행, 1행 남을 때까지 삭제(빈 행은 저장 때 서버가 버림), 통화별 합계, 방향키·Enter 셀 이동 */
const PayItemTable = ({ rows, onChange }: PayItemTableProps) => {
  const { t } = useTranslation();
  const dialog = useDialog();
  const tableRef = useRef<HTMLDivElement>(null);
  const [hoverRowId, setHoverRowId] = useState<string | null>(null);
  const totals = computeCurrencyTotals(rows);
  const deletable = rows.length > 1;

  const patch = (id: string, next: Partial<ExpenseVoucherRow>) =>
    onChange(rows.map((r) => (r.id === id ? { ...r, ...next } : r)));

  // 값이 있는 행만 확인 후 삭제(205 와 같음). 통화는 늘 골라져 있어 판단에서 뺌
  const remove = (row: ExpenseVoucherRow) => {
    const doRemove = () => onChange(rows.filter((r) => r.id !== row.id));
    const hasValue = [
      row.category,
      row.summary,
      row.quantity,
      row.amount,
      row.remark,
    ].some((v) => v.trim() !== '');
    if (!hasValue) {
      doRemove();
      return;
    }
    dialog.show({
      message: t(
        '해당 항목을 삭제하시겠습니까?\n삭제 후에는 복구할 수 없습니다.',
      ),
      confirmButtonText: t('삭제'),
      cancelButtonText: t('취소'),
      onConfirm: doRemove,
    });
  };

  const addRow = () => {
    onChange([...rows, createExpenseVoucherRow()]);
    requestAnimationFrame(() => focusCell(tableRef.current, rows.length, 0));
  };

  // 셀 이동 — 입력칸 좌우는 커서가 끝에 있을 때만, 통화 선택은 메뉴 대신 이동
  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const { key } = e;
    const vertical = ['ArrowUp', 'ArrowDown', 'Enter'].includes(key);
    const horizontal = ['ArrowLeft', 'ArrowRight'].includes(key);
    if (!vertical && !horizontal) return;
    if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
    // 한글·중국어 조합을 확정하는 Enter 는 이동하지 않음
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    const el = e.target as HTMLElement;
    const cell = el.closest<HTMLElement>('[data-col-index]');
    const rowEl = el.closest<HTMLElement>('[data-row-index]');
    if (!cell || !rowEl) return;
    const col = Number(cell.dataset.colIndex);
    const row = Number(rowEl.dataset.rowIndex);

    if (horizontal && el instanceof HTMLInputElement) {
      const { selectionStart: start, selectionEnd: end, value } = el;
      if (start === null || start !== end) return;
      if (key === 'ArrowLeft' && start !== 0) return;
      if (key === 'ArrowRight' && start !== value.length) return;
    }
    const nextRow = key === 'ArrowUp' ? row - 1 : vertical ? row + 1 : row;
    const nextCol =
      key === 'ArrowLeft' ? col - 1 : key === 'ArrowRight' ? col + 1 : col;
    if (key === 'Enter') e.preventDefault();
    if (nextCol < 0 || nextCol >= COLUMN_COUNT) return;
    if (nextRow < 0 || nextRow >= rows.length) return;
    e.preventDefault();
    e.stopPropagation();
    focusCell(tableRef.current, nextRow, nextCol);
  };

  return (
    <TableWrap ref={tableRef}>
      <ApprovalFormTable editable className="expense-voucher-item-table">
        <colgroup>
          <col style={{ width: 46 }} />
          <col style={{ width: '14%' }} />
          <col />
          <col style={{ width: '9%' }} />
          <col style={{ width: 96 }} />
          <col style={{ width: '17%' }} />
          <col style={{ width: '17%' }} />
          <col style={{ width: 32 }} />
        </colgroup>
        <thead>
          <tr>
            <th>NO.</th>
            <th>{t('구분')}</th>
            <th>{t('적요')}</th>
            <th>{t('수량')}</th>
            <th>{t('통화')}</th>
            <th>{t('금액')}</th>
            <th>{t('비고')}</th>
            <th aria-label={t('삭제')} />
          </tr>
        </thead>
        <tbody onKeyDownCapture={handleKeyDown}>
          {rows.map((row, index) => {
            return (
              <ApprovalTableRow
                key={row.id}
                deleteHover={deletable && hoverRowId === row.id}
                data-row-index={index}
              >
                <td className="align-center">{index + 1}</td>
                <td data-col-index={0}>
                  <ApprovalTableCellInput
                    value={row.category}
                    inputProps={{ maxLength: 100 }}
                    onChange={(e) =>
                      patch(row.id, { category: e.target.value })
                    }
                  />
                </td>
                <td data-col-index={1}>
                  <ApprovalTableCellInput
                    value={row.summary}
                    inputProps={{ maxLength: 150 }}
                    onChange={(e) => patch(row.id, { summary: e.target.value })}
                  />
                </td>
                <td data-col-index={2}>
                  <ApprovalTableCellInput
                    className="align-right"
                    value={row.quantity}
                    inputProps={{ maxLength: 12, inputMode: 'decimal' }}
                    onChange={(e) =>
                      patch(row.id, {
                        quantity: formatAmountInput(e.target.value),
                      })
                    }
                  />
                </td>
                <td data-col-index={3}>
                  <ApprovalTableCurrencySelect
                    value={row.currency}
                    options={CURRENCY_OPTIONS}
                    onChange={(currency) => patch(row.id, { currency })}
                  />
                </td>
                <td data-col-index={4}>
                  <ApprovalTableCellInput
                    className="align-right"
                    value={row.amount}
                    inputProps={{ maxLength: 20, inputMode: 'decimal' }}
                    onChange={(e) =>
                      patch(row.id, {
                        amount: formatAmountInput(e.target.value),
                      })
                    }
                  />
                </td>
                <td data-col-index={5}>
                  <ApprovalTableCellInput
                    value={row.remark}
                    inputProps={{ maxLength: 150 }}
                    onChange={(e) => patch(row.id, { remark: e.target.value })}
                  />
                </td>
                <td className="align-center">
                  <ApprovalTableRowDeleteButton
                    type="button"
                    aria-label={t('삭제')}
                    disabled={!deletable}
                    onMouseEnter={() => setHoverRowId(row.id)}
                    onMouseLeave={() => setHoverRowId(null)}
                    onClick={() => remove(row)}
                  />
                </td>
              </ApprovalTableRow>
            );
          })}
        </tbody>
        <tfoot>
          {totals.map((total, index) => (
            <tr key={total.currency}>
              {index === 0 && (
                <td colSpan={4} rowSpan={totals.length}>
                  <strong>{t('총 합계')}</strong>
                </td>
              )}
              <td className="align-center">
                <CurrencyLabel currency={total.currency} />
              </td>
              <td className="align-right">
                <strong>{total.total}</strong>
              </td>
              {index === 0 && <td colSpan={2} rowSpan={totals.length} />}
            </tr>
          ))}
        </tfoot>
      </ApprovalFormTable>
      <ApprovalTableAddRowButton type="button" onClick={addRow}>
        <PlusIcon />
        {t('행 추가')}
      </ApprovalTableAddRowButton>
    </TableWrap>
  );
};

export default PayItemTable;
